import React, { useState, useMemo } from 'react';
import {
  Plus, ArrowLeft, Building2, MapPin, Globe2, Mail, Phone,
  Target, CheckCircle2, Pencil, Save,
} from 'lucide-react';
import { Avatar, Badge, Modal, CreatableSelect, ProductMultiSelect, fmtDate } from '../components/ui.jsx';
import DealModal from '../components/DealModal.jsx';
import {
  customSources, stages, locationData,
  stageColor, userById, money, formatRevenue, addSource,
} from '../data.js';

const stageTone = { Lead: 'info', Opportunity: 'info', Customer: 'pass' };

function ContactModal({ contact, onClose, onSave }) {
  const editing = Boolean(contact);
  const [form, setForm] = useState(contact || { name: '', role: '', email: '', phone: '', primary: false });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title={editing ? `Edit ${contact.name}` : 'Add contact'}
      subtitle="Who to reach at this account."
      onClose={onClose}
      actions={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!form.name.trim()} onClick={() => onSave(form)}><Save size={14} />{editing ? 'Save changes' : 'Add contact'}</button></>}
    >
      <label>Full name<input placeholder="e.g. Sarah Mitchell" value={form.name} onChange={set('name')} autoFocus /></label>
      <label>Role / title<input placeholder="e.g. Chief Technology Officer" value={form.role} onChange={set('role')} /></label>
      <div className="modal-form-row">
        <label>Email<input type="email" placeholder="name@company.com" value={form.email} onChange={set('email')} /></label>
        <label>Phone<input placeholder="+1 555 000 0000" value={form.phone} onChange={set('phone')} /></label>
      </div>
      <label className="checkbox-row"><input type="checkbox" checked={!!form.primary} onChange={(e) => setForm({ ...form, primary: e.target.checked })} /> Primary contact for this account</label>
    </Modal>
  );
}

function AccountDetailView({ account, deals, contacts, team, products, onBack, onEdit, onCreateContact, onUpdateContact, onCreateDeal, onUpdateDeal }) {
  const [tab, setTab] = useState('Overview');
  const [contactModal, setContactModal] = useState(null); // null closed, {} = new, object = editing
  const [dealModal, setDealModal] = useState(null);

  const accContacts = contacts.filter((c) => c.accountId === account.id);
  const accDeals = deals.filter((d) => d.accountId === account.id);

  const saveContact = async (form) => {
    if (form.id) await onUpdateContact(account.id, form.id, form);
    else await onCreateContact(account.id, form);
    setContactModal(null);
  };

  const saveDeal = async (form) => {
    if (dealModal?.id) await onUpdateDeal(dealModal.id, form);
    else await onCreateDeal({ ...form, accountId: account.id });
    setDealModal(null);
  };

  return (
    <div>
      <button className="record-back" onClick={onBack}><ArrowLeft size={15} /> Back to accounts</button>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <Avatar name={account.name} color={account.tone} size="lg" />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{account.name}</span>
                <Badge tone={stageTone[account.stage]} dot><CheckCircle2 size={11} style={{ marginRight: 2 }} />{account.stage}</Badge>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Building2 size={13} /> {account.industry} · Since {account.since}
              </div>
            </div>
          </div>
          <div className="account-hero-actions">
            <button className="btn btn-primary" onClick={onEdit}><Pencil size={14} /> Edit account</button>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Account health</div>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{account.health}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {['Overview', 'Contacts', 'Deals'].map((t) => (
          <span key={t} className={`tab-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t}{t === 'Contacts' ? ` (${accContacts.length})` : t === 'Deals' ? ` (${accDeals.length})` : ''}
          </span>
        ))}
      </div>

      {tab === 'Overview' && (
        <section className="detail-grid">
          <div className="card">
            <div className="section-title"><div><Building2 size={16} /><h3>Account details</h3></div></div>
            <div className="field-grid">
              <div><small>Account owner</small><b><Avatar name={userById(team, account.owner)?.name} size="sm" />{userById(team, account.owner)?.name}</b></div>
              <div><small>Industry</small><b>{account.industry}</b></div>
              <div><small>Annual revenue</small><b>{formatRevenue(account.revenueAmount, account.revenueUnit)}</b></div>
              <div><small>Employees</small><b>{account.size}</b></div>
              <div><small>Website</small><a><Globe2 size={13} />{account.website}</a></div>
              <div><small>Headquarters</small><b><MapPin size={13} />{account.hq}</b></div>
            </div>
          </div>
          <div className="card">
            <div className="section-title"><div><Target size={16} /><h3>Relationship</h3></div></div>
            <div className="field-grid">
              <div><small>Lead source</small><b>{account.source}</b></div>
              <div><small>Lifecycle stage</small><Badge tone={stageTone[account.stage]}>{account.stage}</Badge></div>
              <div><small>Pipeline stage</small><Badge tone="purple">{account.pipelineStage}</Badge></div>
              <div><small>Open deals</small><b>{accDeals.filter((d) => !['Won', 'Lost'].includes(d.stage)).length}</b></div>
              <div><small>Open pipeline</small><b>{money(accDeals.filter((d) => !['Won', 'Lost'].includes(d.stage)).reduce((s, d) => s + d.value, 0))}</b></div>
            </div>
          </div>
        </section>
      )}

      {tab === 'Contacts' && (
        <div className="card">
          <div className="card-header"><div><div className="card-title">Contacts</div></div><button className="btn btn-ghost" onClick={() => setContactModal({})}><Plus size={14} /> Add contact</button></div>
          {accContacts.map((c) => (
            <div key={c.id} className="activity-item" style={{ alignItems: 'center' }}>
              <Avatar name={c.name} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>{c.name} {c.primary && <span className="tag" style={{ marginLeft: 6 }}>Primary</span>}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{c.role}</div>
              </div>
              {c.phone && <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}><Phone size={13} />{c.phone}</span>}
              <a style={{ fontSize: 12, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 5 }}><Mail size={13} />{c.email}</a>
              <button className="btn-icon" onClick={() => setContactModal(c)} title="Edit"><Pencil size={13} /></button>
            </div>
          ))}
          {accContacts.length === 0 && <div className="table-empty">No contacts on file.</div>}
        </div>
      )}

      {tab === 'Deals' && (
        <div className="card">
          <div className="card-header"><div><div className="card-title">Deals</div></div><button className="btn btn-ghost" onClick={() => setDealModal({})}><Plus size={14} /> Add deal</button></div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Product</th><th>Stage</th><th>Value</th><th>Close date</th><th>Owner</th><th></th></tr></thead>
              <tbody>
                {accDeals.map((d) => (
                  <tr key={d.id}>
                    <td className="td-name">{(d.products || []).join(', ')}</td>
                    <td><Badge tone={stageColor[d.stage]}>{d.stage}</Badge></td>
                    <td className="td-mono">{money(d.value)}</td>
                    <td className="td-muted">{fmtDate(d.close)}</td>
                    <td><Avatar name={userById(team, d.owner)?.name} size="sm" /></td>
                    <td><button className="btn-icon" onClick={() => setDealModal(d)} title="Edit"><Pencil size={13} /></button></td>
                  </tr>
                ))}
                {accDeals.length === 0 && <tr><td colSpan={6} className="table-empty">No deals linked yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {contactModal && <ContactModal contact={contactModal.id ? contactModal : null} onClose={() => setContactModal(null)} onSave={saveContact} />}
      {dealModal && (
        <DealModal
          deal={dealModal.id ? dealModal : null}
          account={account}
          accounts={[account]}
          contacts={contacts}
          team={team}
          products={products}
          onClose={() => setDealModal(null)}
          onSave={saveDeal}
        />
      )}
    </div>
  );
}

const blankIfPlaceholder = (value) => (value === '—' ? '' : value);

function AccountModal({ account, onClose, onSave, partners = [], team = [], onCreateTeamMember }) {
  const editing = Boolean(account);
  const [form, setForm] = useState(account ? {
    ...account,
    size: blankIfPlaceholder(account.size),
    website: blankIfPlaceholder(account.website),
    phone: blankIfPlaceholder(account.phone),
  } : {
    name: '', industry: '', hqCountry: '', hqState: '', hqCity: '', website: '', size: '',
    revenueAmount: '', revenueUnit: 'Million', phone: '', notes: '',
    owner: team[0]?.id || '', stage: 'Lead', pipelineStage: 'Proposal', source: 'Internal', health: 65,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ownerOptions = team.map((u) => ({ value: u.id, label: u.name }));
  const sourceOptions = [
    { value: 'Internal', label: 'Internal' },
    ...partners.map((p) => ({ value: p.name, label: p.name })),
    ...customSources.map((s) => ({ value: s, label: s })),
  ];

  const countryOptions = Object.keys(locationData);
  const stateOptions = form.hqCountry ? Object.keys(locationData[form.hqCountry] || {}) : [];
  const cityOptions = form.hqCountry && form.hqState ? (locationData[form.hqCountry]?.[form.hqState] || []) : [];

  return (
    <Modal
      title={editing ? `Edit ${account.name}` : 'Add account'}
      subtitle={editing ? 'Update the company, ownership and pipeline information.' : 'Create a company and add it to the proposal pipeline.'}
      onClose={onClose}
      actions={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!form.name.trim()} onClick={() => onSave(form)}><Save size={14}/>{editing?'Save changes':'Create account'}</button></>}
    >
      <label>Company name<input placeholder="e.g. Acme Technologies" value={form.name} onChange={set('name')} autoFocus /></label>
      <div className="modal-form-row">
        <label>Industry<input placeholder="e.g. Retail" value={form.industry} onChange={set('industry')} /></label>
        <label>Company size<input placeholder="e.g. 500-700" value={form.size} onChange={set('size')} /></label>
      </div>

      <label>Headquarters</label>
      <div className="modal-form-row modal-form-row--3">
        <select value={form.hqCountry} onChange={(e) => setForm({ ...form, hqCountry: e.target.value, hqState: '', hqCity: '' })}>
          <option value="">Country</option>
          {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={form.hqState} disabled={!form.hqCountry} onChange={(e) => setForm({ ...form, hqState: e.target.value, hqCity: '' })}>
          <option value="">{form.hqCountry === 'United Arab Emirates' ? 'Emirate' : 'State'}</option>
          {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={form.hqCity} disabled={!form.hqState} onChange={set('hqCity')}>
          <option value="">City</option>
          {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="modal-form-row">
        <label>Annual revenue
          <div className="input-group">
            <span className="input-group-prefix">$</span>
            <input type="number" min="0" step="0.1" placeholder="0" value={form.revenueAmount} onChange={set('revenueAmount')} />
            <select value={form.revenueUnit} onChange={set('revenueUnit')}>
              <option>Thousand</option>
              <option>Million</option>
              <option>Billion</option>
            </select>
          </div>
        </label>
        <label>Pipeline stage<select value={form.pipelineStage} onChange={set('pipelineStage')}>{stages.map((s) => <option key={s}>{s}</option>)}</select></label>
      </div>

      <div className="modal-form-row">
        <label>Account owner<CreatableSelect value={form.owner} options={ownerOptions} placeholder="e.g. Jordan Lee" onAdd={(name) => onCreateTeamMember(name)} onChange={(v) => setForm({ ...form, owner: v.id ?? v })} /></label>
        <label>Source<CreatableSelect value={form.source} options={sourceOptions} placeholder="e.g. Referral" onAdd={(name) => addSource(name)} onChange={(v) => setForm({ ...form, source: v })} /></label>
      </div>

      <div className="modal-form-row">
        <label>Website<input placeholder="company.com" value={form.website} onChange={set('website')} /></label>
        <label>Phone<input placeholder="Company phone" value={form.phone || ''} onChange={set('phone')} /></label>
      </div>
      <label>Account health<input type="number" min="0" max="100" value={form.health} onChange={set('health')} /></label>
      <label>Internal notes<textarea rows="3" placeholder="Buying context, next steps, risks..." value={form.notes || ''} onChange={set('notes')} /></label>
    </Modal>
  );
}

export default function Accounts({
  accounts = [], contacts = [], deals = [], team = [], products = [], partners = [],
  onCreateAccount, onUpdateAccount, onCreateContact, onUpdateContact, onCreateDeal, onUpdateDeal, onCreateTeamMember,
}) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => accounts.filter((a) => `${a.name} ${a.industry}`.toLowerCase().includes(search.toLowerCase())), [accounts, search]);

  const createAccount = async (form) => {
    const created = await onCreateAccount(form);
    setModal(false);
    return created;
  };

  const saveAccount = async (form) => {
    const updated = await onUpdateAccount(form.id, form);
    setSelected(updated);
    setEditing(null);
  };

  if (selected) return <><AccountDetailView
      account={accounts.find((a) => a.id === selected.id) || selected} deals={deals} contacts={contacts} team={team} products={products}
      onBack={() => setSelected(null)} onEdit={() => setEditing(selected)}
      onCreateContact={onCreateContact} onUpdateContact={onUpdateContact} onCreateDeal={onCreateDeal} onUpdateDeal={onUpdateDeal}
    />{editing && <AccountModal account={editing} partners={partners} team={team} onCreateTeamMember={onCreateTeamMember} onClose={() => setEditing(null)} onSave={saveAccount} />}</>;

  return (
    <>
      <div className="page-head">
        <div className="page-head-left">
          <div className="page-title">Accounts</div>
          <div className="page-subtitle">{accounts.length} companies across leads, opportunities and customers.</div>
        </div>
        <div className="page-actions"><button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={15} /> Add account</button></div>
      </div>

      <div className="card">
        <div className="toolbar"><div className="search-field"><input placeholder="Search accounts..." value={search} onChange={(e) => setSearch(e.target.value)} /></div></div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Company</th><th>Industry</th><th>Lifecycle</th><th>Pipeline stage</th><th>Owner</th><th>Health</th><th>Source</th></tr></thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="clickable" onClick={() => setSelected(a)}>
                  <td>
                    <div className="row-entity">
                      <Avatar name={a.name} color={a.tone} size="sm" />
                      <div><div className="row-entity-name">{a.name}</div><div className="row-entity-sub">{a.hq}</div></div>
                    </div>
                  </td>
                  <td>{a.industry}</td>
                  <td><Badge tone={stageTone[a.stage]}>{a.stage}</Badge></td>
                  <td><Badge tone={stageColor[a.pipelineStage] || 'purple'}>{a.pipelineStage}</Badge></td>
                  <td><Avatar name={userById(team, a.owner)?.name} size="sm" /></td>
                  <td className="td-mono">{a.health}</td>
                  <td className="td-muted">{a.source}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="table-empty">No accounts match your search.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <AccountModal partners={partners} team={team} onCreateTeamMember={onCreateTeamMember} onClose={() => setModal(false)} onSave={createAccount} />}
    </>
  );
}
